import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { useRealtimeSync } from "@/hooks/use-realtime";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";

import NotFound from "@/pages/not-found";
import LandingPage from "@/pages/LandingPage";
import Login from "@/pages/Login";
import Registrar from "@/pages/Registrar";
import EsqueciSenha from "@/pages/EsqueciSenha";
import RedefinirSenha from "@/pages/RedefinirSenha";
import Dashboard from "@/pages/Dashboard";
import Empresas from "@/pages/Empresas";
import Usuarios from "@/pages/Usuarios";
import Laudos from "@/pages/Laudos";
import LaudosPorParceiro from "@/pages/LaudosPorParceiro";
import LaudoDetalhes from "@/pages/LaudoDetalhes";
import Pecas from "@/pages/Pecas";
import Lotes from "@/pages/Lotes";
import LoteDetalhes from "@/pages/LoteDetalhes";
import Convites from "@/pages/Convites";
import Perfil from "@/pages/Perfil";
import PecaPerguntas from "@/pages/PecaPerguntas";
import Financeiro from "@/pages/Financeiro";
import Veiculos from "@/pages/Veiculos";
import RegistrarUsuario from "@/pages/RegistrarUsuario";
import { Auditoria } from "@/pages/Auditoria";
import Configuracoes from "@/pages/Configuracoes";
import FichaTecnica from "@/pages/FichaTecnica";
import Creditos from "@/pages/Creditos";
import ComprarCreditos from "@/pages/ComprarCreditos";
import RetornoCreditos from "@/pages/RetornoCreditos";
import ComprovanteCredito from "@/pages/ComprovanteCredito";
import SimularCheckoutAsaas from "@/pages/SimularCheckoutAsaas";
import AdminVistoriadoras from "@/pages/AdminVistoriadoras";
import AdminModulos from "@/pages/AdminModulos";
import DashboardVendas from "@/pages/DashboardVendas";
import Relatorios from "@/pages/Relatorios";
import MinhasFaturas from "@/pages/MinhasFaturas";
import RetornoFatura from "@/pages/RetornoFatura";
import FaturasParceiro from "@/pages/FaturasParceiro";
import NotaFiscal from "@/pages/NotaFiscal";
import RelatorioParceiro from "@/pages/RelatorioParceiro";
import SplitPayment from "@/pages/SplitPayment";
import Apks from "@/pages/Apks";
import TutoriaisAdmin from "@/pages/TutoriaisAdmin";
import Tutoriais from "@/pages/Tutoriais";
import Solicitacoes from "@/pages/Solicitacoes";
import VerificarLaudo from "@/pages/VerificarLaudo";
import PainelTestes from "@/pages/PainelTestes";

const PAGE_TITLES: Record<string, string> = {
  "": "Início",
  login: "Entrar",
  registrar: "Cadastro de Empresa",
  entrar: "Cadastro de Usuário",
  verificar: "Verificar Laudo",
  dashboard: "Dashboard",
  empresas: "Empresas",
  usuarios: "Usuários",
  laudos: "Laudos",
  pecas: "Peças",
  lotes: "Lotes",
  convites: "Convites",
  perfil: "Perfil",
  financeiro: "Financeiro",
  "minhas-faturas": "Minhas Faturas",
  "faturas-parceiro": "Faturas do Parceiro",
  "nota-fiscal": "Nota Fiscal",
  "painel-testes": "Painel de Testes",
  creditos: "Créditos",
  admin: "Administração",
  owner: "Owner",
  repasses: "Repasses Asaas",
  solicitacoes: "Solicitações",
  vendas: "Vendas",
  relatorios: "Relatórios",
  veiculos: "Veículos",
  auditoria: "Auditoria",
  configuracoes: "Configurações",
  "ficha-tecnica": "Ficha Técnica",
  tutoriais: "Tutoriais",
};

function usePageTitle() {
  const [location] = useLocation();
  useEffect(() => {
    const segment = location.split("/").filter(Boolean)[0] ?? "";
    const title = PAGE_TITLES[segment];
    document.title = title ? `${title} · LaudoLegal` : "LaudoLegal";
  }, [location]);
}

function ProtectedRoute({ component: Component, ...rest }: any) {
  const { user, loading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      setLocation("/");
    }
  }, [loading, user, setLocation]);

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return <Component {...rest} />;
}

function LegacyRedirect({ to }: { to: string }) {
  const [, setLocation] = useLocation();
  useEffect(() => setLocation(to, { replace: true }), [setLocation, to]);
  return null;
}

function Router() {
  usePageTitle();
  return (
    <Switch>
      <Route path="/" component={LandingPage} />
      <Route path="/login" component={Login} />
      <Route path="/registrar" component={Registrar} />
      <Route path="/esqueci-senha" component={EsqueciSenha} />
      <Route path="/redefinir-senha" component={RedefinirSenha} />
      <Route path="/entrar" component={RegistrarUsuario} />
      <Route path="/verificar/:laudoId" component={VerificarLaudo} />
      <Route path="/dashboard">
        {() => <ProtectedRoute component={Dashboard} />}
      </Route>
      <Route path="/empresas">
        {() => <ProtectedRoute component={Empresas} />}
      </Route>
      <Route path="/usuarios">
        {() => <ProtectedRoute component={Usuarios} />}
      </Route>
      <Route path="/laudos">
        {() => <ProtectedRoute component={Laudos} />}
      </Route>
      <Route path="/laudos/por-parceiro">
        {() => <ProtectedRoute component={LaudosPorParceiro} />}
      </Route>
      <Route path="/laudos/:id">
        {(params) => <ProtectedRoute component={LaudoDetalhes} {...params} />}
      </Route>
      <Route path="/pecas">{() => <ProtectedRoute component={Pecas} />}</Route>
      <Route path="/lotes">{() => <ProtectedRoute component={Lotes} />}</Route>
      <Route path="/lotes/:id">
        {(params) => <ProtectedRoute component={LoteDetalhes} {...params} />}
      </Route>
      <Route path="/convites">
        {() => <ProtectedRoute component={Convites} />}
      </Route>
      <Route path="/pecas/perguntas">
        {() => <ProtectedRoute component={PecaPerguntas} />}
      </Route>
      <Route path="/perfil">
        {() => <ProtectedRoute component={Perfil} />}
      </Route>
      <Route path="/tutoriais">
        {() => <ProtectedRoute component={Tutoriais} />}
      </Route>
      <Route path="/financeiro">
        {() => <ProtectedRoute component={Financeiro} />}
      </Route>
      <Route path="/minhas-faturas">
        {() => <ProtectedRoute component={MinhasFaturas} />}
      </Route>
      <Route path="/minhas-faturas/retorno">
        {() => <RetornoFatura />}
      </Route>
      <Route path="/faturas-parceiro">
        {() => <ProtectedRoute component={FaturasParceiro} />}
      </Route>
      <Route path="/faturas-parceiro/:id/relatorio">
        {(params) => <ProtectedRoute component={RelatorioParceiro} {...params} />}
      </Route>
      <Route path="/painel-testes">
        {() => <ProtectedRoute component={PainelTestes} />}
      </Route>
      <Route path="/nota-fiscal">
        {() => <ProtectedRoute component={NotaFiscal} />}
      </Route>
      <Route path="/creditos">
        {() => <ProtectedRoute component={Creditos} />}
      </Route>
      <Route path="/creditos/comprar">
        {() => <ProtectedRoute component={ComprarCreditos} />}
      </Route>
      <Route path="/creditos/retorno">
        {() => <ProtectedRoute component={RetornoCreditos} />}
      </Route>
      <Route path="/creditos/comprovante/:id">
        {(params) => <ProtectedRoute component={ComprovanteCredito} {...params} />}
      </Route>
      <Route path="/creditos/simular-checkout/:id">
        {(params) => <ProtectedRoute component={SimularCheckoutAsaas} {...params} />}
      </Route>
      <Route path="/admin/vistoriadoras">
        {() => <ProtectedRoute component={AdminVistoriadoras} />}
      </Route>
      <Route path="/admin/modulos">
        {() => <ProtectedRoute component={AdminModulos} />}
      </Route>
      <Route path="/owner/repasses">
        {() => <ProtectedRoute component={SplitPayment} />}
      </Route>
      <Route path="/repasses">
        {() => <ProtectedRoute component={SplitPayment} />}
      </Route>
      <Route path="/owner/split-payment">
        {() => <LegacyRedirect to="/owner/repasses" />}
      </Route>
      <Route path="/owner/apks">
        {() => <ProtectedRoute component={Apks} />}
      </Route>
      <Route path="/owner/tutoriais">
        {() => <ProtectedRoute component={TutoriaisAdmin} />}
      </Route>
      <Route path="/extrato-split">
        {() => <LegacyRedirect to="/repasses" />}
      </Route>
      <Route path="/solicitacoes">
        {() => <ProtectedRoute component={Solicitacoes} />}
      </Route>
      <Route path="/vendas">
        {() => <ProtectedRoute component={DashboardVendas} />}
      </Route>
      <Route path="/relatorios">
        {() => <ProtectedRoute component={Relatorios} />}
      </Route>
      <Route path="/veiculos">
        {() => <ProtectedRoute component={Veiculos} />}
      </Route>
      <Route path="/auditoria">
        {() => <ProtectedRoute component={Auditoria} />}
      </Route>
      <Route path="/configuracoes">
        {() => <ProtectedRoute component={Configuracoes} />}
      </Route>
      <Route path="/ficha-tecnica">
        {() => <ProtectedRoute component={FichaTecnica} />}
      </Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function RealtimeSync() {
  useRealtimeSync();
  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RealtimeSync />
        <TooltipProvider>
          <Router />
          <Toaster />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
